import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PAYMENT_TRANSACTION_DETAILS } from "@/Graphql/Queries";
import { generateMockData } from "@/Graphql/MockData";
import client from "@/Graphql/apolloClient";
import JSZip from "jszip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  FileText,
  Calendar,
  Clock,
  Eye,
  Download,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
// import { formatCurrency, formatDate } from "@/lib/utils";

// TODO: Replace asset imports when assets are copied
// import paymentProof1 from "@assets/Cash-Payment-Slip-Template-edit-online_1756831142164.png";
// import paymentProof2 from "@assets/Plus-Blue-Payslips-scaled-e1658908007772_1756831142165.webp";
// import paymentProof3 from "@assets/pay-slip-format-scaled_1756831142165.jpg";

// Placeholder image URLs until assets are copied
const paymentProof1 = "https://via.placeholder.com/400x600/10b981/ffffff?text=Payment+Proof+1";

interface PaymentProofViewProps {
  base_path: string;
}

// Utility functions
const formatCurrency = (amount: number, currencyCode: string = '') => {
  // If no currency code provided, fall back to decimal formatting
  if (!currencyCode) {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  // Use currency code display so locales like INR show `INR` instead of the symbol `₹`.
  // Using `currencyDisplay: 'code'` will also display `MYR`, `AUD`, etc., matching other pages.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount as number);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default function PaymentProofView({ base_path }: PaymentProofViewProps) {
  const [searchParams] = useSearchParams();
  const [showFullImage, setShowFullImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [paymentTransactionData, setPaymentTransactionData] = useState<any>(null);

  // Get campaign ID and access token
  const campaignId = searchParams.get('campaign') || '';
  const token = localStorage.getItem("authToken") || '';

  console.log("Payment Proof View - Campaign ID:", campaignId);
  console.log("Payment Proof View - Auth Token:", token ? "Present" : "Missing");

  // Fetch payment transaction details
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!campaignId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let transactionData = null;

        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(PAYMENT_TRANSACTION_DETAILS);
          transactionData = mockResponse.data?.paymentTransactionDetails;
        } else {
          const { data } = await client.query({
            query: PAYMENT_TRANSACTION_DETAILS,
            variables: {
              accessToken: token,
              campaignId: campaignId
            },
            fetchPolicy: "network-only"
          });
          transactionData = data?.paymentTransactionDetails;
        }

        setPaymentTransactionData(transactionData);
        setError(null);
      } catch (err) {
        console.error("Error fetching payment transaction details:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [campaignId, token]);

  console.log("Payment Transaction Query State - Loading:", loading, "Error:", error, "Data:", paymentTransactionData);
  const campaign = paymentTransactionData?.campaign;

  // Map campaign data from PAYMENT_TRANSACTION_DETAILS API
  const campaignData = campaign ? {
    id: campaign.id,
    name: campaign.name,
    code: campaign.dealId || `Campaign ${campaign.id}`,
    duration: `${campaign.startDate?.dateStr || ''} - ${campaign.endDate?.dateStr || ''}`,
    startDate: campaign.startDate?.date || '',
    endDate: campaign.endDate?.date || '',
    campaignNotes: "Campaign payment proof submitted for review",
    inventories: (campaign.inventoriesSummary?.classicInventories || 0) +
      (campaign.inventoriesSummary?.digitalInventories || 0),
    packages: campaign.inventoriesSummary?.packages || 0,
    status: campaign.campaignStatus || "Payment Under Review",
    impressions: campaign.summaryReport?.potentialViews || 0,
    totalPrice: campaign.priceSummary?.netTotal || 0,
    subTotal: campaign.priceSummary?.subTotal || 0,
    grossTotal: campaign.priceSummary?.grossTotal || 0,
    gst: campaign.priceSummary?.tax?.value || 0,
    taxName: campaign.priceSummary?.tax?.name || 'N/A',
    taxPercent: campaign.priceSummary?.tax?.percent || 0,
    currency: campaign.priceSummary?.currency?.symbol || 'N/A',
    currencyCode: campaign.priceSummary?.currency?.code || 'N/A',
    couponCode: campaign.priceSummary?.couponName || null,
    discountPercent: campaign.priceSummary?.discountPercent || 0,
    discountAmount: campaign.priceSummary?.discountedPrice || 0,
    // Payment proof data from assets - now supporting multiple assets
    assets: paymentTransactionData?.assets || [],
    paymentProofUrl: paymentTransactionData?.assets?.[0]?.filePath || paymentProof1,
    paymentProofFilename: paymentTransactionData?.assets?.[0]?.fileName || "Payment_Receipt_Uploaded.png",
    uploadedAt: campaign?.createdDate || new Date().toISOString(),
    lastModifiedDate: campaign?.lastModifiedDate || '',
    // Payment transaction details
    paymentId: paymentTransactionData?.paymentId || '',
    paymentReferenceId: paymentTransactionData?.paymentReferenceId || '',
    paymentType: paymentTransactionData?.paymentType || 'OFFLINE_BANK_TRANSFER',
    paymentStatus: paymentTransactionData?.paymentStatus || 'PENDING',
    paymentProviderMetadata: paymentTransactionData?.paymentProviderMedata || null,
    // Campaign metrics
    uniqueReach: campaign.summaryReport?.uniqueReach || 0,
    averageFrequency: campaign.summaryReport?.averageFrequency || 0,
    cpm: campaign.summaryReport?.cpm || 0,
    totalSpots: campaign.summaryReport?.totalSpots || 0,
    // Additional campaign info
    dsp: campaign.dsp || '',
    bookingSource: campaign.bookingSource || '',
    buyerDetails: campaign.buyerDetails || null,
    campaignInventories: campaign.campaignInventories || []
  } : null;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Payment Details...</h1>
          <p className="text-gray-600">Please wait while we fetch your payment proof information.</p>
        </div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Details Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error ? `Error: ${error.message}` : "The requested payment details could not be found."}
          </p>
          <Link to={`${base_path}/my-campaigns`}>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const handleDownload = async () => {
    // Download all payment proof assets as a zip file
    const assets = campaignData?.assets || [];

    if (assets.length === 0) {
      alert('No payment proof files available for download');
      return;
    }

    try {
      const zip = new JSZip();
      const folder = zip.folder('payment-proofs');

      // Fetch all assets and add to zip
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        try {
          const response = await fetch(asset.filePath);
          const blob = await response.blob();
          const fileName = asset.fileName || `payment-proof-${i + 1}.png`;
          folder?.file(fileName, blob);
        } catch (error) {
          console.error(`Failed to fetch asset ${i + 1}:`, error);
        }
      }

      // Generate zip file and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment-proof-${campaignData?.code || campaignData?.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Zip creation failed:', error);
      alert('Failed to create zip file. Please try again.');
    }
  };

  // --- INSERT THE NEW CODE HERE ---
  // Helper to determine current asset details
  const currentAsset = campaignData?.assets?.[currentImageIndex];
  const assetUrl = currentAsset?.filePath || campaignData?.paymentProofUrl;
  const assetName = currentAsset?.fileName || campaignData?.paymentProofFilename;

  // Check if current asset is a PDF
  const isPdf = assetName?.toLowerCase().endsWith('.pdf') || assetUrl?.toLowerCase().endsWith('.pdf');
  // --------------------------------

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header with Back Button on Right */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-emerald-600">Payment Proof Review</h1>
          <p className="text-gray-600 mt-2">Review uploaded payment proof for campaign approval</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-amber-100 text-amber-800 px-3 py-1">
            <Clock className="h-4 w-4 mr-2" />
            Payment Under Review
          </Badge>
          <Link to={`${base_path}/my-campaigns`}>
            <Button
              variant="outline"
              size="sm"
              className="bg-white/80 backdrop-blur-sm border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Payment Proof Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-600">
                <FileText className="h-5 w-5 text-emerald-600" />
                Payment Proof Document
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="h-5 w-5 text-emerald-600" />
                  <div>
                    <h3 className="font-medium text-emerald-800">Awaiting Approval</h3>
                    <p className="text-sm text-emerald-700">Payment proof has been submitted for review</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Uploaded:</span>
                    <span className="font-medium">{getTimeAgo(campaignData?.assets[0]?.createdDate) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">File Name:</span>
                    <span className="font-medium">{campaignData.assets[currentImageIndex]?.fileName || campaignData.paymentProofFilename}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="text-amber-700 font-medium">Under Review</span>
                  </div>
                </div>
              </div>

              {/* Asset Count - Show when multiple assets */}
              {campaignData.assets.length > 1 && (
                <div className="text-center text-sm text-gray-600 font-medium">
                  {campaignData.assets.length} payment proof {campaignData.assets.length === 1 ? 'photo' : 'photos'}
                </div>
              )}

              {/* Payment Proof Carousel */}
              <div className="relative">
                <div
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-emerald-300 transition-colors group"
                  onClick={() => setShowFullImage(true)}
                >
                  {isPdf ? (
                    <div className="w-full h-64 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden relative">
                      {/* PDF Icon Overlay for clarity */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center z-0 opacity-50">
                        <FileText className="h-16 w-16 text-emerald-600/50 mb-2" />
                        <span className="text-emerald-600/50 font-medium">PDF Document</span>
                      </div>
                      {/* Iframe for preview - pointerEvents: none ensures the click passes to the parent div to open the modal */}
                      <iframe
                        src={`${assetUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full object-contain relative z-10"
                        style={{ pointerEvents: 'none' }}
                        title="PDF Preview"
                      />
                    </div>
                  ) : (
                    <img
                      src={assetUrl}
                      alt={`Payment proof ${currentImageIndex + 1}`}
                      className="w-full h-64 object-contain rounded-lg group-hover:scale-105 transition-transform"
                    />
                  )}

                  {/* Click indicator overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded-lg">
                    <div className="bg-white/90 rounded-full p-3 shadow-lg">
                      <Eye className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>

                  {/* Navigation Arrows - Show only when multiple assets */}
                  {campaignData.assets.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) =>
                            prev === 0 ? campaignData.assets.length - 1 : prev - 1
                          );
                        }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) =>
                            prev === campaignData.assets.length - 1 ? 0 : prev + 1
                          );
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Image Counter - Always show when assets exist */}
                  {campaignData.assets.length > 0 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {currentImageIndex + 1} / {campaignData.assets.length}
                    </div>
                  )}
                </div>

                {/* <p className="text-center text-sm text-gray-600 mt-2">
                  <Eye className="h-4 w-4 inline mr-1" />
                  Click to view full document
                </p> */}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowFullImage(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Full
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Details */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-emerald-600">Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Campaign Name</div>
                <div className="text-gray-900 font-semibold">{campaignData.name}</div>
                <div className="text-sm text-gray-500">{campaignData.code}</div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>{formatDate(campaignData.startDate)} - {formatDate(campaignData.endDate)}</span>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Campaign Notes</div>
                <div className="text-sm text-gray-700">{campaignData.campaignNotes}</div>
              </div>

              {/* Campaign Metrics - Dynamic Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {campaignData.inventories > 0 && (
                  <div>
                    <div className="text-gray-500">Inventories</div>
                    <div className="font-medium">{campaignData.inventories} {campaignData.inventories === 1 ? 'Inventory' : 'Inventories'}</div>
                  </div>
                )}
                {campaignData.packages > 0 && (
                  <div>
                    <div className="text-gray-500">Packages</div>
                    <div className="font-medium">{campaignData.packages} {campaignData.packages === 1 ? 'Package' : 'Packages'}</div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Impressions</div>
                  <div className="font-medium">{campaignData.impressions?.toLocaleString() || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Unique Reach</div>
                  <div className="font-medium">{campaignData.uniqueReach?.toLocaleString() || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Avg. Frequency</div>
                  <div className="font-medium">{campaignData.averageFrequency?.toFixed(2) || 'N/A'}</div>
                </div>
              </div>

              <Separator />

              {/* Payment Summary */}
              <div className="space-y-3">
                <h3 className="font-medium text-emerald-600">Payment Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Gross Total</span>
                    <span>{formatCurrency(campaignData.grossTotal, campaignData.currencyCode)}</span>
                  </div>
                  {campaignData.couponCode && (
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-600">🎫 Coupon Applied ({campaignData.couponCode})</span>
                      <span className="text-blue-600 font-medium">Applied</span>
                    </div>
                  )}
                  {campaignData.discountAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-600">Discount Applied{campaignData.discountPercent > 0 ? ` (${Math.round(campaignData.discountPercent)}%)` : ''}</span>
                      <span className="text-emerald-600 font-medium">- {formatCurrency(campaignData.discountAmount, campaignData.currencyCode)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Subtotal (Excl. {campaignData.taxName})</span>
                    <span>{formatCurrency(campaignData.subTotal, campaignData.currencyCode)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{campaignData.taxName} ({Math.round(campaignData.taxPercent)}%)</span>
                    <span>{formatCurrency(campaignData.gst, campaignData.currencyCode)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Grand Total</span>
                    <span className="text-emerald-600">{formatCurrency(campaignData.totalPrice, campaignData.currencyCode)}</span>
                  </div>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-600 mb-3">Review Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="text-sm">
                      <div className="font-medium">Payment Proof Uploaded</div>
                      <div className="text-gray-500">{getTimeAgo(campaignData.uploadedAt)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    <div className="text-sm">
                      <div className="font-medium">Under Review</div>
                      <div className="text-gray-500">Media owner reviewing payment</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                    <div className="text-sm text-gray-500">
                      <div>Approval Decision</div>
                      <div className="text-xs">Pending review completion</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Information Card */}
      <Card className="mt-8">
        <CardContent className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-emerald-600 mb-2">Review Process Information</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>&bull; Payment proof has been submitted to the media owner for verification</p>
              <p>&bull; Review typically takes 24-48 hours during business days</p>
              <p>&bull; You will be notified via email once the review is complete</p>
              <p>&bull; Campaign will be automatically activated upon payment approval</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Full Image Modal with Carousel */}
      {showFullImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative w-[1000px] h-[700px] bg-white rounded-lg p-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-lg font-semibold">
                Payment Proof - {campaignData.assets[currentImageIndex]?.fileName || campaignData.paymentProofFilename}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFullImage(false)}
              >
                ✕
              </Button>
            </div>
            <div className="relative flex-1 min-h-0 bg-gray-50 rounded-lg overflow-hidden">
              {isPdf ? (
                <iframe 
                  src={assetUrl}
                  className="w-full h-full"
                  title="Full Payment Proof PDF"
                />
              ) : (
                <img 
                  src={assetUrl}
                  alt={`Payment proof ${currentImageIndex + 1} full view`}
                  className="w-full h-full object-contain"
                />
              )}

              {/* Navigation Arrows in Modal */}
              {campaignData.assets.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex((prev) =>
                        prev === 0 ? campaignData.assets.length - 1 : prev - 1
                      );
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-6 w-6 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex((prev) =>
                        prev === campaignData.assets.length - 1 ? 0 : prev + 1
                      );
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-6 w-6 text-gray-700" />
                  </button>

                  {/* Image Counter in Modal */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} / {campaignData.assets.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}